// Derived statement variance (the Change column). All money stays in integer
// minor units (cents) — only the percentage is a ratio, computed from two
// integers and never accumulated, so it cannot drift.
//
// The comparative columns a statement returns are ordered most-recent-first:
// index 0 is the report's own period/date, index 1 is the immediate
// comparison. A change compares column 0 against column 1.

interface StatementChange {
  /** Column 0 minus column 1, in integer minor units. */
  absoluteCents: number;
  /** `(current - base) / |base| * 100`, or `null` when the base is zero. */
  percent: number | null;
  /** `true` good, `false` bad, `null` when flat or not directional. */
  favorable: boolean | null;
}

/**
 * Section labels where a rise is bad news (expenses, costs, liabilities).
 * Everything else (revenue, assets, equity, net income) is better when it
 * rises. Matched case-insensitively on the section label.
 */
export function higherIsBetter(sectionLabel: string): boolean {
  const label = sectionLabel.toLowerCase();
  return !(
    label.includes("expense") ||
    label.includes("cost") ||
    label.includes("liabilit") ||
    label.includes("payable") ||
    label.includes("debt")
  );
}

/**
 * Change between two columns. `favorableWhenUp` flips the tone for sections
 * where an increase is undesirable (expenses, liabilities).
 */
export function computeChange(
  currentCents: number | null | undefined,
  baseCents: number | null | undefined,
  options: { favorableWhenUp?: boolean } = {},
): StatementChange | null {
  if (currentCents === null || currentCents === undefined) return null;
  if (baseCents === null || baseCents === undefined) return null;
  const favorableWhenUp = options.favorableWhenUp ?? true;
  const absoluteCents = currentCents - baseCents;
  const percent =
    baseCents === 0 ? null : (absoluteCents / Math.abs(baseCents)) * 100;
  const favorable =
    absoluteCents === 0 ? null : absoluteCents > 0 === favorableWhenUp;
  return { absoluteCents, percent, favorable };
}

/** `+12.5%` / `-4.0%`; zero base renders as `—` (no ratio from nothing). */
export function formatPercent(percent: number | null): string {
  if (percent === null) return "—";
  const rounded = Math.round(percent * 10) / 10;
  return `${rounded > 0 ? "+" : ""}${rounded.toFixed(1)}%`;
}
